import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from "@chakra-ui/react";
import NextArrowIcon from "assets/img/layout/arrow.svg";
import PrevArrowIcon from "assets/img/layout/previou-arrow.svg";
import { useCallback, useMemo } from "react";

type Props = {
  totalCount: number;
  totalPages: number;
  currentPage: number;
  perPage: number;
  onChangePage: (page: number) => void;
  onSetPageLimit?: (pageSize: number) => void;
  showPageSizeSelector?: boolean;
};

type PageItem = number | "ellipsis";

const buildPages = (totalPages: number, currentPage: number): PageItem[] => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const safeCurrent = Math.min(Math.max(1, currentPage), totalPages);

  if (safeCurrent <= 3) {
    return [1, 2, 3, "ellipsis", totalPages];
  }

  if (safeCurrent >= totalPages - 2) {
    return [1, "ellipsis", totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "ellipsis", safeCurrent - 1, safeCurrent, safeCurrent + 1, "ellipsis", totalPages];
};

const handleEllipsisClick = (currentPage: number, totalPages: number, direction: "prev" | "next"): number => {
  const jumpSize = totalPages < 10 ? 3 : 5;
  if (direction === "prev") {
    return Math.max(1, currentPage - jumpSize);
  } else {
    return Math.min(totalPages, currentPage + jumpSize);
  }
};

export default function CustomerPaginations(props: Props) {
  const {
    totalCount,
    totalPages,
    currentPage,
    onChangePage,
    perPage,
    onSetPageLimit,
    showPageSizeSelector = true,
  } = props;

  const shadow = "0px 4px 30px rgba(0, 0, 0, 0.1)";

  const perPageList = useMemo(() => {
    if (totalCount < 10) return [10];
    if (totalCount < 20) return [10, 20];
    if (totalCount < 30) return [10, 20, 30];
    return [10, 20, 30, 50];
  }, [totalCount]);

  const pageItems = useMemo(() => buildPages(totalPages, currentPage), [totalPages, currentPage]);

  const handlePrev = useCallback(() => {
    onChangePage(currentPage > 1 ? currentPage - 1 : 1);
  }, [currentPage, onChangePage]);

  const handleNext = useCallback(() => {
    onChangePage(currentPage < totalPages ? currentPage + 1 : totalPages);
  }, [currentPage, onChangePage, totalPages]);

  return (
    <Box mt={0} display="flex" justifyContent="center" position="relative" zIndex={111}>
      {showPageSizeSelector && (
        <Menu>
          <MenuButton
            as={Button}
            rightIcon={<ChevronDownIcon />}
            sx={{
              marginRight: "10px",
              textAlign: "left",
              padding: "15px 16px",
              borderRadius: "10px",
              background: "#FFF",
              border: "1px solid #E0E0E0",
              fontSize: "15px",
              fontWeight: "400",
              lineHeight: "normal",
              fontFamily: "Inter",
              "&[data-active]": {
                backgroundColor: "#EFEFEF",
              },
            }}
          >
            <Flex alignItems="center">
              <Text
                fontSize="14px"
                lineHeight="24px"
                color="#000"
                boxSizing="border-box"
              >
                {perPage}
              </Text>
            </Flex>
          </MenuButton>

          <MenuList
            boxShadow={shadow}
            p="0px"
            mt="10px"
            minW="80px"
            borderRadius="0px"
            bg="#FFF"
            border="none"
          >
            <Flex
              flexDirection="column"
              p="5px 0px"
              fontSize="14px"
              lineHeight="24px"
              fontWeight="400"
              color="black"
            >
              {perPageList.map((option, index) => (
                <MenuItem
                  key={option}
                  _hover={{ bg: "#EFEFEF" }}
                  borderRadius="0px"
                  padding="5px 10px"
                  onClick={() => onSetPageLimit?.(option)}
                  borderBottom={index < perPageList.length - 1 ? "1px solid #F3F5F5" : ""}
                >
                  <Text fontSize="sm">{option}</Text>
                </MenuItem>
              ))}
            </Flex>
          </MenuList>
        </Menu>
      )}

      <ButtonGroup
        sx={{
          boxShadow: "0px 1px 2px 0px #1018280D",
          border: "1px solid #E5E7EB",
          borderRadius: "10px",
          overflow: "hidden",
          svg: {
            width: "24px !important",
            height: "24px !important",
          },
          button: {
            backgroundColor: "#FFF",
            width: "40px",
            height: "38px",
            border: "none",
            borderLeft: "1px solid #E5E7EB",
            borderRadius: "0px",
            fontWeight: "400",
            fontSize: "16px",
            color: "#0B0E10",
            "&:first-of-type": {
              borderLeft: "none",
            },
            "&+button": {
              marginLeft: "0px",
            },
            "&.active": {
              backgroundColor: "#F5F7FA",
              color: "#0B0E10",
            },
            "&:hover:not(.active)": {
              backgroundColor: "#fff",
            },
            "&[data-disabled]": {
              opacity: 0.4,
              cursor: "not-allowed",
            },
          },
        }}
      >
        <Button padding={0} onClick={handlePrev} isDisabled={currentPage <= 1} aria-label="Previous page">
          <Box as="img" src={PrevArrowIcon} alt="Previous" w="18px" h="18px" />
        </Button>

        {pageItems.map((item, idx) => {
          if (item === "ellipsis") {
            // Determine which ellipsis this is (prev or next)
            const isPrevEllipsis = idx < pageItems.length / 2;
            const ellipsisPage = handleEllipsisClick(currentPage, totalPages, isPrevEllipsis ? "prev" : "next");
            
            return (
              <Button 
                key={`ellipsis-${idx}`} 
                fontSize="16px" 
                onClick={() => onChangePage(ellipsisPage)}
                aria-label={`Jump ${isPrevEllipsis ? "back" : "forward"} ${totalPages < 10 ? 3 : 5} pages`}
              >
                ...
              </Button>
            );
          }

          return (
            <Button
              key={item}
              className={item === currentPage ? "active" : ""}
              onClick={() => onChangePage(item)}
              fontSize="16px"
              aria-label={`Page ${item}`}
            >
              {item}
            </Button>
          );
        })}

        <Button
          onClick={handleNext}
          isDisabled={currentPage >= totalPages}
          aria-label="Next page"
          padding={0}
        >
          <Box as="img" src={NextArrowIcon} alt="Next" p="0" w="18px" h="18px" />
        </Button>
      </ButtonGroup>
    </Box>
  );
}
